/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Fragment, memo } from 'react';

import { isAuthenticatedUser } from 'js/lib/user';

import { Grid, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { Recommender } from '@coursera/event-pulse-types';

import useIsBrowse from 'bundles/collections-common/utils/useIsBrowse';
import { getShouldShowUnregisteredOnboarding } from 'bundles/coursera-plus-common/utils/subscriptionsForwardDiscoveryExperiment';
import InlineLearningGoalQuestion from 'bundles/onboarding-common/components/InlineLearningGoalQuestion';
import ModularOnboardingModuleRenderer from 'bundles/onboarding-common/module-renderers/ModularOnboardingModuleRenderer';
import SearchCards from 'bundles/search-common/components/search-cards/SearchCards';
import {
  SEARCH_RESULTS_BEFORE_INLINE_ONBOARDING,
  SEARCH_RESULTS_BEFORE_LOGGED_IN_INLINE_ONBOARDING,
  SEARCH_RESULTS_BEFORE_ONBOARDING_FOR_FOUR_OR_TWO_CARD_PER_ROW,
} from 'bundles/search-common/constants';
import type { SearchHit, SearchUpsellHit } from 'bundles/search-common/providers/searchTypes';
import {
  isInBrowseFilterTestVariant,
  isInEQPFilterTestVariant,
  isInFilterTestVariant,
} from 'bundles/search-common/utils/experimentUtils';
import { shouldShowFreeQueryPageConfigPreview } from 'bundles/search-common/utils/pageConfigUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';

const styles = {
  inlineLearningGoalQuestionWrapper: css`
    padding: var(--cds-spacing-300) 6px var(--cds-spacing-600);
    ${breakpoints.down('xs')} {
      padding: 0 var(--cds-spacing-50) var(--cds-spacing-300);
    }
  `,
  onboardingModuleContainer: css`
    div {
      margin-top: 0;
    }
  `,
};

type SearchPageBodySectionsProps = {
  hits: Array<SearchHit | SearchUpsellHit>;
  resultsPerPage: number;
  forceOpenInNewTab: boolean;
  eventingData?: {
    searchIndex: string;
    searchRecommender?: Recommender;
    searchIndexPosition: number;
    page?: string;
    searchQuery?: string;
    filtersApplied?: Record<string, string[]>;
  };
  isLoading?: boolean;
  isSearchPage: boolean;
  onScrolledToBottom?: () => void;
  totalHitsDisplayed?: number;
  cardIndexOffset?: number;
  isFirstSection?: boolean;
  page?: number;
  isSeoEntityPagePath?: boolean;
  filterStatusTags?: (tags: string[]) => string[];
};

// Shared props for SearchCards to reduce duplication
type SearchCardsBaseProps = {
  resultsPerPage: number;
  forceOpenInNewTab: boolean;
  eventingData?: {
    searchIndex: string;
    searchRecommender?: Recommender;
    searchIndexPosition: number;
    page?: string;
    searchQuery?: string;
    filtersApplied?: Record<string, string[]>;
  };
  isLoading?: boolean;
  isSearchPage: boolean;
  onScrolledToBottom?: () => void;
  totalHitsDisplayed?: number;
  isSeoEntityPagePath?: boolean;
  isLastSection?: boolean;
  filterStatusTags?: (tags: string[]) => string[];
};

type OnboardingConditions = {
  showLoggedOutInlineOnboarding: boolean;
  showLoggedInInlineOnboarding: boolean;
};

// Hook to determine onboarding conditions
const useOnboardingConditions = (
  query: string | undefined,
  page: number | undefined,
  isSeoEntityPagePath: boolean | undefined,
  isFirstSection: boolean | undefined
): OnboardingConditions => {
  const shouldShowLoggedOutOnboardingForPage = (page === 1 && Boolean(isSeoEntityPagePath)) || Boolean(isFirstSection);
  const shouldShowLoggedInOnboardingForPage = page === 1 && Boolean(isSeoEntityPagePath);

  const showLoggedInInlineOnboarding =
    isAuthenticatedUser() && !shouldShowFreeQueryPageConfigPreview(query) && shouldShowLoggedInOnboardingForPage;

  const showLoggedOutInlineOnboarding =
    getShouldShowUnregisteredOnboarding() &&
    !shouldShowFreeQueryPageConfigPreview(query) &&
    shouldShowLoggedOutOnboardingForPage;

  return { showLoggedOutInlineOnboarding, showLoggedInInlineOnboarding };
};

// Component for rendering search cards with onboarding
type SearchSectionProps = SearchCardsBaseProps & {
  section: Array<SearchHit | SearchUpsellHit>;
  cardIndexOffset?: number;
  onboardingConditions: OnboardingConditions;
  isLastSection?: boolean;
};

const SearchSection = ({
  section,
  cardIndexOffset,
  onboardingConditions,
  isLastSection = false,
  ...searchCardsProps
}: SearchSectionProps) => {
  const { showLoggedOutInlineOnboarding, showLoggedInInlineOnboarding } = onboardingConditions;
  const isConsumerSearchPage = useIsConsumerSerp();
  const isBrowsePage = useIsBrowse();
  const isEqpPage = useIsEqp();
  const isInFilterExperiment =
    (isEqpPage && isInEQPFilterTestVariant()) ||
    (isBrowsePage && isInBrowseFilterTestVariant()) ||
    (isConsumerSearchPage && isInFilterTestVariant());
  const isExtraLargeScreenForFilterExperiment = useMediaQuery(breakpoints.up('lg')) && isInFilterExperiment;
  const isLargeScreenForFilterExperiment = useMediaQuery(breakpoints.between('md', 'lg')) && isInFilterExperiment;
  const isSmallSplitPointForLoggedInOnboarding =
    useMediaQuery(breakpoints.between('xs', 'sm')) && showLoggedInInlineOnboarding;

  // Helper to render SearchCards with consistent props
  const renderSearchCards = (
    hits: Array<SearchHit | SearchUpsellHit>,
    indexOffset?: number,
    isEndOfSplitSection?: boolean,
    isFirstHalfOfSplitSection?: boolean,
    shouldShowLoading?: boolean
  ) => (
    <SearchCards
      hits={hits}
      cardIndexOffset={indexOffset}
      isEndOfSplitSection={isEndOfSplitSection}
      isFirstHalfOfSplitSection={isFirstHalfOfSplitSection}
      isLastSection={shouldShowLoading ?? isLastSection}
      {...searchCardsProps}
    />
  );

  // No onboarding - simple case
  if (!showLoggedOutInlineOnboarding && !showLoggedInInlineOnboarding) {
    return renderSearchCards(section, cardIndexOffset, false, false, isLastSection);
  }

  // Determine split point and onboarding component
  let splitPoint: number;
  if (isExtraLargeScreenForFilterExperiment) {
    splitPoint = SEARCH_RESULTS_BEFORE_ONBOARDING_FOR_FOUR_OR_TWO_CARD_PER_ROW;
  } else if (isLargeScreenForFilterExperiment) {
    splitPoint = showLoggedInInlineOnboarding
      ? SEARCH_RESULTS_BEFORE_LOGGED_IN_INLINE_ONBOARDING
      : SEARCH_RESULTS_BEFORE_INLINE_ONBOARDING;
  } else if (isInFilterExperiment && isSmallSplitPointForLoggedInOnboarding) {
    splitPoint = SEARCH_RESULTS_BEFORE_ONBOARDING_FOR_FOUR_OR_TWO_CARD_PER_ROW;
  } else if (isSmallSplitPointForLoggedInOnboarding) {
    splitPoint = SEARCH_RESULTS_BEFORE_ONBOARDING_FOR_FOUR_OR_TWO_CARD_PER_ROW;
  } else if (showLoggedOutInlineOnboarding) {
    splitPoint = SEARCH_RESULTS_BEFORE_INLINE_ONBOARDING;
  } else {
    splitPoint = SEARCH_RESULTS_BEFORE_LOGGED_IN_INLINE_ONBOARDING;
  }

  const firstHalf = section.slice(0, splitPoint);
  const secondHalf = section.slice(splitPoint);

  const onboardingComponent = (
    <div>
      <Grid container css={styles.onboardingModuleContainer}>
        <Grid item xs={12}>
          <ModularOnboardingModuleRenderer
            entrypoint="SEARCH"
            shouldShowSuccessMessageOnCompletion
            noPageGridContainer
          />
        </Grid>
      </Grid>
      {showLoggedOutInlineOnboarding && (
        <div css={styles.inlineLearningGoalQuestionWrapper}>
          <InlineLearningGoalQuestion isCompact />
        </div>
      )}
    </div>
  );

  const secondHalfOffset = cardIndexOffset ? cardIndexOffset + splitPoint : splitPoint;

  return (
    <Fragment>
      {renderSearchCards(firstHalf, cardIndexOffset, false, true, false)}
      {onboardingComponent}
      {renderSearchCards(secondHalf, secondHalfOffset, true, false, isLastSection)}
    </Fragment>
  );
};

const SearchPageBodySections = ({
  hits,
  resultsPerPage,
  forceOpenInNewTab,
  eventingData,
  isLoading = false,
  isSearchPage,
  onScrolledToBottom,
  totalHitsDisplayed,
  cardIndexOffset,
  isFirstSection,
  page,
  isSeoEntityPagePath,
  filterStatusTags,
}: SearchPageBodySectionsProps) => {
  const query = eventingData?.searchQuery;
  const onboardingConditions = useOnboardingConditions(query, page, isSeoEntityPagePath, isFirstSection);

  if (!Array.isArray(hits)) return null;

  // Split hits into sections
  const sections: Array<Array<SearchHit | SearchUpsellHit>> = [];
  for (let i = 0; i < hits.length; i += resultsPerPage) {
    sections.push(hits.slice(i, i + resultsPerPage));
  }
  if (sections.length === 0 && isLoading) sections.push([]);

  const searchCardsBaseProps: SearchCardsBaseProps = {
    resultsPerPage,
    forceOpenInNewTab,
    eventingData,
    isLoading,
    isSearchPage,
    onScrolledToBottom,
    totalHitsDisplayed,
    isSeoEntityPagePath,
    filterStatusTags,
  };

  return (
    <div>
      {sections.map((section, index) => {
        const sectionOffset = cardIndexOffset ? cardIndexOffset + index * resultsPerPage : undefined;
        const isLastSection = index === sections.length - 1;

        return (
          <div key={`section-${section[0]?.id}`}>
            <SearchSection
              section={section}
              cardIndexOffset={sectionOffset}
              onboardingConditions={onboardingConditions}
              isLastSection={isLastSection}
              {...searchCardsBaseProps}
            />
          </div>
        );
      })}
    </div>
  );
};

export default memo(SearchPageBodySections);
